import React from 'react';
import {
  Modal,
  Button,
  Select,
  Typography,
  Form,
  Checkbox,
} from '@crisellevaldez/speculo-ui';
import { useUniqueContactTags } from '../../../hooks/contacts/useUniqueContactTags';
import { SearchableSelect } from '../../common/SearchableSelect';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { buildUserOptions } from '../../../utils/userOptions';
import { useFlywheelEnabled, useOrganizationStore } from '../../../stores/useOrganizationStore';
import { useOrgFlowOptions } from '../../../hooks/graphql/flows/useOrgFlowOptions';

// Note: Recent Outcome filter is exposed as a quick-filter dropdown in
// ContactTableHeader (CONTACT_OUTCOMES list lives there). Keep the outcome
// state plumbed through this modal so callers' existing values pass through
// unchanged when Apply is clicked, but no UI is rendered here for it.

type FilterState = {
  selectedTags: string[];
  /** When true, return only contacts that have ALL `selectedTags`
   *  (AND). When false (default), match ANY tag (OR). Backend already
   *  supports the `matchAllTags` parameter — see `useContacts.ts`. */
  matchAllTags?: boolean;
  dateRange: { startDate: Date | null; endDate: Date | null };
  outcome: string[];
  leadSource: string[];
  teamMember?: string;
  leadIntent?: string[];
  has_ealerts_registered?: boolean | null;
  hasEmailError?: boolean | null;
  flywheelOptOut?: boolean | null;
  aiPaused?: boolean | null;
  flowIds?: string[];
  flowTypes?: string[];
};

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFilter: (filters: FilterState) => void;
  initialFilters: FilterState;
}

export const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  onFilter,
  initialFilters,
}) => {
  const { data: tags = [], isLoading: isTagsLoading, hasMore: tagsHasMore, loadMore: tagsLoadMore, setSearch: tagsSetSearch } = useUniqueContactTags(isOpen);
  const { users, loading: usersLoading } = useOrganizationUsers();
  const flywheelEnabled = useFlywheelEnabled();
  const { organization } = useOrganizationStore();
  const {
    options: flowOptions,
    flowTypes: availableFlowTypes,
    isLoading: isFlowOptionsLoading,
  } = useOrgFlowOptions(organization?.id, isOpen);
  const [selectedTags, setSelectedTags] = React.useState<string[]>(initialFilters.selectedTags);
  const [matchAllTags, setMatchAllTags] = React.useState<boolean>(
    initialFilters.matchAllTags ?? false,
  );
  const [dateRange, setDateRange] = React.useState(initialFilters.dateRange);
  const [outcome, setOutcome] = React.useState<string[]>(initialFilters.outcome);
  const [leadSource, setLeadSource] = React.useState<string[]>(initialFilters.leadSource || []);
  const [teamMember, setTeamMember] = React.useState<string | undefined>(initialFilters.teamMember);
  const [leadIntent, setLeadIntent] = React.useState<string[]>(initialFilters.leadIntent || []);
  const [hasEalertsRegistered, setHasEalertsRegistered] = React.useState<boolean | null>(
    initialFilters.has_ealerts_registered || null
  );
  const [hasEmailError, setHasEmailError] = React.useState<boolean | null>(
    initialFilters.hasEmailError || null
  );
  const [flywheelOptOut, setFlywheelOptOut] = React.useState<boolean | null>(
    initialFilters.flywheelOptOut ?? null
  );
  const [aiPaused, setAiPaused] = React.useState<boolean | null>(
    initialFilters.aiPaused || null
  );
  const [flowIds, setFlowIds] = React.useState<string[]>(initialFilters.flowIds || []);
  const [flowTypes, setFlowTypes] = React.useState<string[]>(initialFilters.flowTypes || []);

  // Update state when initialFilters change
  React.useEffect(() => {
    setSelectedTags(initialFilters.selectedTags);
    setMatchAllTags(initialFilters.matchAllTags ?? false);
    setDateRange(initialFilters.dateRange);
    setOutcome(initialFilters.outcome);
    setLeadSource(initialFilters.leadSource || []);
    setTeamMember(initialFilters.teamMember);
    setLeadIntent(initialFilters.leadIntent || []);
    setHasEalertsRegistered(initialFilters.has_ealerts_registered || null);
    setHasEmailError(initialFilters.hasEmailError || null);
    setFlywheelOptOut(initialFilters.flywheelOptOut ?? null);
    setAiPaused(initialFilters.aiPaused || null);
    setFlowIds(initialFilters.flowIds || []);
    setFlowTypes(initialFilters.flowTypes || []);
  }, [initialFilters]);

  const handleReset = () => {
    const emptyFilters: FilterState = {
      selectedTags: [],
      matchAllTags: false,
      dateRange: { startDate: null, endDate: null },
      outcome: [],
      leadSource: [],
      teamMember: undefined,
      leadIntent: [],
      has_ealerts_registered: null,
      hasEmailError: null,
      flywheelOptOut: null,
      aiPaused: null,
      flowIds: [],
      flowTypes: [],
    };
    setSelectedTags(emptyFilters.selectedTags);
    setMatchAllTags(emptyFilters.matchAllTags ?? false);
    setDateRange(emptyFilters.dateRange);
    setOutcome(emptyFilters.outcome);
    setLeadSource(emptyFilters.leadSource);
    setTeamMember(emptyFilters.teamMember);
    setLeadIntent(emptyFilters.leadIntent ?? []);
    setHasEalertsRegistered(emptyFilters.has_ealerts_registered ?? null);
    setHasEmailError(emptyFilters.hasEmailError ?? null);
    setFlywheelOptOut(emptyFilters.flywheelOptOut ?? null);
    setAiPaused(emptyFilters.aiPaused ?? null);
    setFlowIds([]);
    setFlowTypes([]);
    onFilter(emptyFilters);
    onClose();
  };

  const handleFilter = () => {
    onFilter({
      selectedTags,
      matchAllTags,
      dateRange,
      outcome,
      leadSource,
      teamMember,
      leadIntent,
      has_ealerts_registered: hasEalertsRegistered,
      hasEmailError,
      flywheelOptOut,
      aiPaused,
      flowIds,
      flowTypes,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <Typography variant="h5">Advanced Filters</Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Form.Control className="flex-1">
              <Form.Label>Select Tag(s)</Form.Label>
              <SearchableSelect
                placeholder="Select tags"
                value={selectedTags}
                onChange={(value) => setSelectedTags(value)}
                options={tags.map((tag) => ({ value: tag, label: tag }))}
                isLoading={isTagsLoading}
                hasMore={tagsHasMore}
                onLoadMore={tagsLoadMore}
                onSearch={tagsSetSearch}
                searchPlaceholder="Search tags..."
                multiple
              />
              <div className="flex items-center gap-2 mt-2">
                <Checkbox
                  id="match_all_tags"
                  checked={matchAllTags}
                  onChange={(e) => setMatchAllTags(e.target.checked)}
                />
                <Form.Label
                  htmlFor="match_all_tags"
                  className="cursor-pointer flex items-center my-0 text-xs text-muted-foreground"
                >
                  Match all selected tags (AND). Otherwise any (OR).
                </Form.Label>
              </div>
            </Form.Control>

            <Form.Control className="flex-1">
              <Form.Label>Assigned To</Form.Label>
              <Select
                placeholder="Select team member"
                value={teamMember}
                onChange={(value) => setTeamMember(value as string)}
                options={buildUserOptions(users)}
                isLoading={usersLoading}
                searchable
                className="py-2.5"
              />
            </Form.Control>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Form.Control className="flex-1">
              <Form.Label>Lead Source</Form.Label>
              <Select
                placeholder="Select options"
                value={leadSource}
                onChange={(value) => setLeadSource(value as string[])}
                options={[
                  { value: 'Email', label: 'Email' },
                  { value: 'PPC', label: 'PPC' },
                  { value: 'Organic', label: 'Organic' },
                ]}
                multiple
                searchable
              />
            </Form.Control>

            <Form.Control className="flex-1">
              <Form.Label>Intent</Form.Label>
              <Select
                placeholder="Select intent"
                value={leadIntent}
                onChange={(value) => setLeadIntent(value as string[])}
                options={[
                  { value: 'Buyer', label: 'Buyer' },
                  { value: 'Seller', label: 'Seller' },
                ]}
                multiple
                searchable
              />
            </Form.Control>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Form.Control className="flex-1">
              <Form.Label>Flow</Form.Label>
              <Select
                placeholder="Select flow(s)"
                value={flowIds}
                onChange={(value) => setFlowIds(value as string[])}
                options={flowOptions}
                isLoading={isFlowOptionsLoading}
                multiple
                searchable
              />
            </Form.Control>

            <Form.Control className="flex-1">
              <Form.Label>Flow Type</Form.Label>
              <Select
                placeholder="Select flow type(s)"
                value={flowTypes}
                onChange={(value) => setFlowTypes(value as string[])}
                options={availableFlowTypes.map((t) => ({ value: t, label: t }))}
                isLoading={isFlowOptionsLoading}
                multiple
                searchable
              />
            </Form.Control>
          </div>

          {/* Checkbox toggles — laid out 2-per-row on sm+ so each box sits
              on its own line within a two-column grid. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="has_email_error"
                checked={hasEmailError === true}
                onChange={(e) => setHasEmailError(e.target.checked)}
              />
              <Form.Label htmlFor="has_email_error" className="cursor-pointer flex items-center my-0">
                Invalid Email Address
              </Form.Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="has_ealerts_registered"
                checked={hasEalertsRegistered === true}
                onChange={(e) => setHasEalertsRegistered(e.target.checked)}
              />
              <Form.Label htmlFor="has_ealerts_registered" className="cursor-pointer flex items-center my-0">
                E-Alert Subscription Active
              </Form.Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="ai_paused"
                checked={aiPaused === true}
                onChange={(e) => setAiPaused(e.target.checked ? true : null)}
              />
              <Form.Label htmlFor="ai_paused" className="cursor-pointer flex items-center my-0">
                AI Paused
              </Form.Label>
            </div>
            {flywheelEnabled && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="flywheel_enrolled"
                  checked={flywheelOptOut === false}
                  onChange={(e) => setFlywheelOptOut(e.target.checked ? false : null)}
                />
                <Form.Label
                  htmlFor="flywheel_enrolled"
                  className="cursor-pointer flex items-center my-0"
                >
                  In Follow-up Flywheel
                </Form.Label>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="primary" onClick={handleFilter}>
            Filter
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
